import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { creditsFromRaw } from "@/features/credits/format";
import { Tabs } from "@/features/evals/components";
import {
  type BalanceChange,
  type HistoryWindow,
  SPEND_ORDER,
  type SpendBy,
  spendBySteps,
} from "./balances";
import { ChartControls } from "./charts/ChartControls";
import { PeriodStepper, stepButton } from "./charts/PeriodStepper";
import { StackedBars } from "./charts/StackedBars";
import {
  type BucketStep,
  bucketInterval,
  formatBucketStart,
  formatChartCredits,
  formatPeriod,
  formatSpan,
} from "./charts/shared";
import { Credits, Note } from "./components";
import { bucketLabels } from "./format";

export const GRAPH_RANGES = ["loaded", "1d", "7d", "30d"] as const;
type GraphRange = (typeof GRAPH_RANGES)[number];
export type DayRange = Exclude<GraphRange, "loaded">;

/** A time graph's settings as the page address keeps them, each missing at its default. */
export interface GraphSettings {
  /** The graph is open. */
  graph?: true;
  /** Missing for loaded pages. */
  graphRange?: DayRange;
  /** How many ranges back the day range shows; missing for the newest. */
  graphBack?: number;
  /** The player graph shows spend rather than the balance. */
  graphSpend?: true;
  /** How spend stacks; missing for the graph's first option. */
  spendBy?: SpendBy;
  /** The balance's axis runs from zero. */
  graphFromZero?: true;
  /** "Fit typical" is on. */
  graphFit?: true;
  /** The span zoomed into, as instants. */
  zoomFrom?: string;
  zoomTo?: string;
}

/** A span the graph zoomed into, and the step it shows in. */
export interface Zoom {
  from: number;
  to: number;
  step: BucketStep;
}

const rangeLabels: Record<GraphRange, string> = {
  loaded: "Loaded pages",
  "1d": "1 day",
  "7d": "7 days",
  "30d": "30 days",
};

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
/** The shortest span a drag zooms into. */
const SHORTEST_ZOOM_MS = 5 * MINUTE_MS;

const rangeDays: Record<DayRange, number> = { "1d": 1, "7d": 7, "30d": 30 };
export const rangeSteps: Record<DayRange, BucketStep> = {
  "1d": "hour",
  "7d": "sixHours",
  "30d": "day",
};

/** Five minutes across three hours or less, hours across two days, six hours across two weeks, then days. */
export const stepFor = (spanMs: number): BucketStep =>
  spanMs <= 3 * HOUR_MS
    ? "fiveMinutes"
    : spanMs <= 2 * DAY_MS
      ? "hour"
      : spanMs <= 14 * DAY_MS
        ? "sixHours"
        : "day";

export const formatCredits = (credits: number) =>
  credits.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 });

/** The span a day range covers `back` ranges before the one ending `now`. */
function daysPeriod(range: DayRange, back: number, now: number) {
  const length = rangeDays[range] * DAY_MS;
  const to = now - back * length;
  return { from: to - length, to };
}

const monthName = new Intl.DateTimeFormat(undefined, { month: "short" });
const clockTime = new Intl.DateTimeFormat(undefined, {
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/**
 * A whole UTC day as the day bars name it; otherwise the span in the viewer's time, short enough
 * to stay on one line when it crosses days: "Sep 28 11:00 – 29 13:00".
 */
function zoomLabel({ from, to }: Zoom) {
  const start = new Date(from);
  const end = new Date(to);
  if (to - from === DAY_MS && bucketInterval.day.floor(start).valueOf() === from) {
    return formatBucketStart(start, "day");
  }
  if (start.toDateString() === new Date(to - 1).toDateString()) return formatSpan(start, end);
  const sameMonth =
    start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  const endDay = sameMonth ? `${end.getDate()}` : `${monthName.format(end)} ${end.getDate()}`;
  return `${monthName.format(start)} ${start.getDate()} ${clockTime.format(start)} – ${endDay} ${clockTime.format(end)}`;
}

/** Escape belongs to whatever has focus when that is a field or a dialog. */
function escapeIsTaken(event: KeyboardEvent) {
  const target = event.target;
  return (
    event.defaultPrevented ||
    (target instanceof HTMLElement &&
      (target.isContentEditable ||
        target.closest("input, textarea, select, dialog, [role=dialog]") !== null))
  );
}

/** The zoomed span, arrows that step it by its own length up to now, and a way back out. */
function ZoomedSpan({
  zoom,
  now,
  onStep,
  onClear,
}: {
  zoom: Zoom;
  now: number;
  onStep: (zoom: Zoom) => void;
  onClear: () => void;
}) {
  const length = zoom.to - zoom.from;
  const shift = (by: number) => onStep({ ...zoom, from: zoom.from + by, to: zoom.to + by });
  return (
    <div className="inline-flex items-center gap-0.5 rounded-full bg-foreground/5 p-0.5 text-xs">
      <button
        type="button"
        aria-label="Previous span"
        title="Previous span"
        onClick={() => shift(-length)}
        className={stepButton}
      >
        <ChevronLeft className="size-3.5" aria-hidden />
      </button>
      <span aria-live="polite" className="min-w-0 px-1 whitespace-nowrap tabular-nums">
        {zoomLabel(zoom)}
      </span>
      <button
        type="button"
        aria-label="Next span"
        title="Next span"
        disabled={zoom.to >= now}
        onClick={() => shift(length)}
        className={stepButton}
      >
        <ChevronRight className="size-3.5" aria-hidden />
      </button>
      <button
        type="button"
        aria-label="Zoom out"
        title="Zoom out"
        onClick={onClear}
        className={stepButton}
      >
        <X className="size-3.5" aria-hidden />
      </button>
    </div>
  );
}

/** Which range a time graph shows, and the span it is zoomed into. */
export interface GraphFrame {
  range: GraphRange;
  /** The day range's period, or null for loaded pages. */
  period: { from: number; to: number } | null;
  now: number;
  /** An earlier period than the newest. */
  stepped: boolean;
  zoom: Zoom | null;
  onZoom: (zoom: Zoom) => void;
  /** "Fit typical" is on. */
  fit: boolean;
  onFitChange: (fit: boolean) => void;
}

/** The span the address zooms into, when it names one that runs forward. */
function addressZoom({ zoomFrom, zoomTo }: GraphSettings): Zoom | null {
  if (!zoomFrom || !zoomTo) return null;
  const from = Date.parse(zoomFrom);
  const to = Date.parse(zoomTo);
  return from < to ? { from, to, step: stepFor(to - from) } : null;
}

const zoomSettings = (zoom: Zoom | null) => ({
  zoomFrom: zoom ? new Date(zoom.from).toISOString() : undefined,
  zoomTo: zoom ? new Date(zoom.to).toISOString() : undefined,
});

/**
 * A graph over time with range tabs: loaded pages, or day ranges stepped back through earlier
 * periods. Dragging zooms in, the arrows step the zoomed span, Escape goes back to the view before,
 * and the cross or another range zooms back out. The settings live in the page address; the views
 * Escape goes back through live only as long as the page.
 */
export function TimeGraph({
  settings,
  onSettingsChange,
  leading,
  controls,
  children,
}: {
  settings: GraphSettings;
  onSettingsChange: (patch: Partial<GraphSettings>) => void;
  /** Before the range tabs. */
  leading?: ReactNode;
  /** After the period or zoomed span. */
  controls: ReactNode;
  children: (frame: GraphFrame) => ReactNode;
}) {
  const range = settings.graphRange ?? "loaded";
  const back = settings.graphBack ?? 0;
  const zoom = addressZoom(settings);
  /** The zoomed spans before this one, newest last. */
  const [earlier, setEarlier] = useState<Zoom[]>([]);
  const [now] = useState(Date.now);
  const period = range === "loaded" ? null : daysPeriod(range, back, now);
  const zoomedIn = zoom !== null;
  useEffect(() => {
    if (!zoomedIn) return;
    const zoomOut = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !escapeIsTaken(event)) {
        setEarlier(earlier.slice(0, -1));
        onSettingsChange(zoomSettings(earlier.at(-1) ?? null));
      }
    };
    window.addEventListener("keydown", zoomOut);
    return () => window.removeEventListener("keydown", zoomOut);
  }, [zoomedIn, earlier, onSettingsChange]);
  return (
    <div className="space-y-3 pb-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {leading}
        <Tabs
          compact
          value={range}
          options={(Object.keys(rangeLabels) as GraphRange[]).map(value => ({
            value,
            label: rangeLabels[value],
          }))}
          onChange={next => {
            setEarlier([]);
            onSettingsChange({
              graphRange: next === "loaded" ? undefined : next,
              graphBack: undefined,
              ...zoomSettings(null),
            });
          }}
        />
      </div>
      <ChartControls
        controls={
          <>
            {zoom ? (
              <ZoomedSpan
                zoom={zoom}
                now={now}
                onStep={next => onSettingsChange(zoomSettings(next))}
                onClear={() => {
                  setEarlier([]);
                  onSettingsChange(zoomSettings(null));
                }}
              />
            ) : (
              range !== "loaded" &&
              period && (
                <PeriodStepper
                  back={back}
                  label={formatPeriod(
                    new Date(period.from),
                    new Date(period.to),
                    rangeSteps[range],
                  )}
                  onChange={next => onSettingsChange({ graphBack: next || undefined })}
                />
              )
            )}
            {controls}
          </>
        }
      >
        {children({
          range,
          period,
          now,
          stepped: back > 0,
          zoom,
          onZoom: next => {
            if (zoom) setEarlier([...earlier, zoom]);
            onSettingsChange(zoomSettings(next));
          },
          fit: settings.graphFit === true,
          onFitChange: fit => onSettingsChange({ graphFit: fit || undefined }),
        })}
      </ChartControls>
    </div>
  );
}

/** One page of a day-at-a-time read: the UTC day it starts, and whether it reached the start. */
interface ReadDay {
  from: number;
  reachedStart: boolean;
}

/**
 * Reads a day at a time back from today until `from`, or the span zoomed into when earlier, is
 * covered. `progress` reads how far it has got while it is still reading.
 */
export function useDaysCovering<Day extends ReadDay>(
  days: {
    data?: { pages: Day[] };
    hasNextPage: boolean;
    isFetching: boolean;
    fetchNextPage: () => Promise<unknown>;
  },
  today: string,
  from: number,
  zoom: Zoom | null,
) {
  const earliest = Math.min(from, zoom?.from ?? from);
  const needed = Math.ceil((Date.parse(today) - earliest) / DAY_MS) + 1;
  const read = days.data?.pages ?? [];
  const more = days.hasNextPage && read.length < needed;
  const { fetchNextPage, isFetching } = days;
  useEffect(() => {
    if (more && !isFetching) void fetchNextPage();
  }, [more, isFetching, fetchNextPage]);
  const last = read.at(-1);
  return {
    read,
    needed,
    knownFrom: !last || last.reachedStart ? Number.NEGATIVE_INFINITY : last.from,
    progress: more ? `Loading day ${read.length + 1} of ${needed}…` : null,
  };
}

/** The span a graph shows, how far back it is known, and how it steps. */
export interface GraphSpan {
  from: number;
  knownFrom: number;
  step: BucketStep;
  /** When the chart ends: now, or the end of an earlier period. */
  to: number;
  now: number;
  /** An earlier period, so its last balance is the one it ended on rather than now's. */
  stepped: boolean;
  progress: string | null;
}

/** The span to draw once zoomed, up to now, or a note when none of it is loaded. */
export function zoomedSpan<Span extends GraphSpan>(span: Span, zoom: Zoom | null) {
  const shown = zoom
    ? {
        ...span,
        from: zoom.from,
        to: Math.min(zoom.to, span.now),
        step: zoom.step,
        stepped: zoom.to < span.now,
      }
    : span;
  const note =
    shown.to <= shown.knownFrom ? <Note>{span.progress ?? "Not loaded"}</Note> : undefined;
  return { shown, note };
}

export type Drag = (from: number, to: number) => void;

/** Zooms to a dragged span long enough to be worth it, stepping as suits its length. */
export const dragZoom =
  (onZoom: (zoom: Zoom) => void): Drag =>
  (from, to) => {
    if (to - from >= SHORTEST_ZOOM_MS) onZoom({ from, to, step: stepFor(to - from) });
  };

const spendByLabels: Record<SpendBy, string> = {
  game: "By game",
  player: "By player",
  bucket: "By credit type",
};

/** Tabs for the ways spending can stack. */
export function SpendByTabs({
  options,
  value,
  onChange,
}: {
  options: readonly SpendBy[];
  value: SpendBy;
  onChange: (by: SpendBy) => void;
}) {
  return (
    <Tabs
      compact
      value={value}
      options={options.map(option => ({ value: option, label: spendByLabels[option] }))}
      onChange={onChange}
    />
  );
}

/**
 * What model calls charged each step, stacked by `by`. Clicking a bar zooms into its span and
 * highlights it; a five-minute bar, the shortest, picks its span instead.
 */
export function SpendBars({
  changes,
  from,
  knownFrom,
  step,
  to,
  progress,
  by,
  onSelect,
  onHighlight,
  onZoom,
  fit,
  onFitChange,
}: GraphSpan &
  Pick<GraphFrame, "fit" | "onFitChange"> & {
    changes: readonly BalanceChange[];
    by: SpendBy;
    onSelect?: (window: HistoryWindow) => void;
    /** The span of a bar a click zoomed into, whose rows already shown stand out. */
    onHighlight?: (window: HistoryWindow) => void;
    onZoom: Drag;
  }) {
  const interval = bucketInterval[step];
  const starts = interval
    .range(interval.floor(new Date(from)), new Date(to))
    .filter(start => interval.offset(start, 1).valueOf() > knownFrom);
  const spend = spendBySteps(changes, starts, to, by);
  const keys =
    by === "bucket"
      ? [...[...SPEND_ORDER].reverse(), "moderation" as const].filter(bucket =>
          spend.keys.includes(bucket),
        )
      : spend.keys;
  return (
    <StackedBars
      title="Spend over time"
      series={keys.map(key => ({
        key,
        label:
          by === "bucket"
            ? bucketLabels[key as keyof typeof bucketLabels]
            : (spend.labels.get(key) ?? "Deleted game"),
      }))}
      buckets={starts.map((start, index) => ({
        start: start.toISOString(),
        values: keys.map(key => creditsFromRaw(spend.steps[index]?.(key) ?? "0")),
      }))}
      step={step}
      summary={
        <>
          <Credits raw={spend.total} /> spent on model calls
          {progress && <span className="text-muted-foreground"> · {progress}</span>}
        </>
      }
      format={formatCredits}
      formatTick={formatChartCredits}
      fitted={fit}
      onFittedChange={onFitChange}
      onSelect={index => {
        const start = starts[index];
        const span = { from: start?.valueOf() ?? from, to: starts[index + 1]?.valueOf() ?? to };
        if (start && step !== "fiveMinutes") {
          onHighlight?.(span);
          onZoom(start.valueOf(), interval.offset(start, 1).valueOf());
        } else {
          onSelect?.(span);
        }
      }}
      onZoom={onZoom}
    />
  );
}
